<template>
  <a-form-item
    class="form-phone-field stc-ant-field-skin"
    label="Телефон"
    name="phone"
    :required="required"
  >
    <a-input
      ref="phoneInputRef"
      class="form-phone-field__input"
      type="tel"
      inputmode="numeric"
      :value="phoneDisplay"
      placeholder="+7 (960) 512-32-16"
      @input="onPhoneInput"
      @keydown="onPhoneKeydown"
      @paste="onPhonePaste"
    />
  </a-form-item>
</template>

<script lang="ts" setup>

import { nextTick, ref, watch } from 'vue';
import {
  formatRussianPhoneMaskValue,
  normalizeRussianPhoneDigits,
} from '@/js/utils/initializePhoneMask';

const props = withDefaults(
  defineProps<{
    modelValue: string;
    required?: boolean;
  }>(),
  { required: true },
);

const emit = defineEmits<{
  'update:modelValue': [value: string];
}>();

const phoneInputRef = ref<{ setSelectionRange: (start: number, end: number) => void } | null>(null);
const phoneDisplay = ref('');

const PHONE_ALLOWED_KEYS = new Set([
  'Backspace',
  'Delete',
  'Tab',
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp',
  'ArrowDown',
  'Home',
  'End',
  'Enter',
]);

watch(
  () => props.modelValue,
  (digits) => {
    if (!digits) {
      phoneDisplay.value = '';
      return;
    }
    phoneDisplay.value = formatRussianPhoneMaskValue(digits);
  },
  { immediate: true },
);

const restorePhoneCursor = (beforeCursorDigits: number) => {
  nextTick(() => {
    const afterValue = phoneDisplay.value;
    let pos = afterValue.length;
    let digitsCount = 0;

    for (let i = 0; i < afterValue.length; i++) {
      if (/\d/.test(afterValue[i]!)) digitsCount++;
      if (digitsCount === beforeCursorDigits) {
        pos = i + 1;
        break;
      }
    }

    pos = Math.min(pos, afterValue.length);
    phoneInputRef.value?.setSelectionRange(pos, pos);
  });
};

const applyPhoneValue = (originalValue: string, selectionStart: number) => {
  const beforeCursorDigits = originalValue.slice(0, selectionStart).replace(/\D/g, '').length;

  phoneDisplay.value = formatRussianPhoneMaskValue(originalValue);
  emit('update:modelValue', normalizeRussianPhoneDigits(originalValue));
  restorePhoneCursor(beforeCursorDigits);
};

const onPhoneInput = (e: Event) => {
  const input = e.target as HTMLInputElement;
  applyPhoneValue(input.value, input.selectionStart ?? 0);
};

const onPhoneKeydown = (e: KeyboardEvent) => {
  if (e.ctrlKey || e.metaKey || e.altKey) {
    return;
  }

  if (PHONE_ALLOWED_KEYS.has(e.key)) {
    return;
  }

  if (/^\d$/.test(e.key)) {
    return;
  }

  e.preventDefault();
};

const onPhonePaste = (e: ClipboardEvent) => {
  e.preventDefault();

  const input = e.target as HTMLInputElement;
  const pasted = e.clipboardData?.getData('text') ?? '';
  const selectionStart = input.selectionStart ?? 0;
  const selectionEnd = input.selectionEnd ?? selectionStart;
  const nextValue = `${input.value.slice(0, selectionStart)}${pasted}${input.value.slice(selectionEnd)}`;

  applyPhoneValue(nextValue, selectionStart);
};
</script>
